// Scrolls the page while a card is being dragged (organize mode), two ways:
// - near the top or bottom edge, a little faster the closer the finger, once per frame:
//   SortableJS's own autoscroll moved the page in 15 px steps on a timer of its own, which
//   read as jerky;
// - with a second finger, which scrolls the page as it would outside a drag (as when
//   arranging the apps of the iOS home screen), the card staying under the first one, with
//   some momentum once it lifts.
// SortableJS checks what lies under the still finger every 50 ms (fallback mode): the
// cards would rearrange as the page goes by: the card is filed only once the page stops
// (scrollingByHand), and never twice under a finger that has not moved (allowsSwap).
//
// SortableJS follows every pointer on iOS (pointer events): a second finger's moves would
// drag the card along and its lift would drop it. They are stopped before SortableJS's own
// listeners (window, capture phase) and only scroll the page.
export class DragScroll {
  constructor() {
    this.others = new Map()
    this.dragPointerId = null
    this.dragging = false
    this.options = { capture: true, passive: false }
  }

  attach() {
    for (const type of TYPES) window.addEventListener(type, this, this.options)
  }

  detach() {
    this.stop()
    for (const type of TYPES) window.removeEventListener(type, this, this.options)
  }

  start() {
    this.dragging = true
    // No browser gesture while a card is carried: a second finger read as the start of a
    // pinch made iOS cancel the first one (pointercancel), which SortableJS took for a drop.
    document.documentElement.style.touchAction = 'none'
    // Read once per drag rather than every frame: the native bars do not change meanwhile.
    const styles = getComputedStyle(document.documentElement)
    this.safeTop = parseFloat(styles.getPropertyValue('--safe-area-top')) || 0
    this.safeBottom = parseFloat(styles.getPropertyValue('--safe-area-bottom')) || 0
    this.lastFrame = null
    this.frame = requestAnimationFrame(this.tick)
  }

  stop() {
    this.dragging = false
    document.documentElement.style.removeProperty('touch-action')
    this.others.clear()
    this.momentum = 0
    this.edgeCurrent = 0
    this.edgeMoving = false
    this.aimed = null
    this.shown = null
    this.pointerY = null
    this.fingerAt = null
    this.pointerAt = null
    this.swappedWith = null
    this.swapAt = null
    this.dragTouchId = null
    this.velocity = 0
    this.lastMove = null
    this.handScrolled = 0
    this.handStoppedAt = null
    this.edgeMovedAt = null
    this.card?.remove()
    this.card = null
    cancelAnimationFrame(this.frame)
  }

  // Fingers are followed through touch events: during a two-finger gesture, iOS gave a
  // second pointer's moves positions swinging between both fingers (hundreds of pixels
  // back and forth). Touches keep an identifier and a position of their own.
  handleEvent(event) {
    if (event.type.startsWith('touch')) this.handleTouch(event)
    else this.handlePointer(event)
  }

  handleTouch(event) {
    if (event.type === 'touchstart') {
      if (!this.dragging && event.touches.length === 1) {
        this.dragTouchId = event.touches[0].identifier
        this.fingerAt = [event.touches[0].clientX, event.touches[0].clientY]
        this.notePointer(...this.fingerAt)
      }
      if (this.dragging) {
        for (const touch of event.changedTouches) {
          if (touch.identifier !== this.dragTouchId) {
            this.others.set(touch.identifier, touch.clientY)
            this.velocity = 0
            this.lastMove = event.timeStamp
          }
        }
      }
      return
    }
    if (event.type === 'touchend' || event.type === 'touchcancel') {
      for (const touch of event.changedTouches) {
        // The page carries on, slowing down, as after a native scroll. Not after a pause.
        if (!this.others.delete(touch.identifier) || !this.dragging) continue

        if (event.timeStamp - this.lastMove < 80) {
          const speed = Math.min(Math.abs(this.velocity) * MOMENTUM_SHARE, MAX_MOMENTUM)
          this.momentum = Math.sign(this.velocity) * speed
        }
        this.handStoppedAt = performance.now()
      }
      return
    }

    // touchmove. The page moves by our hand only, never under the native scroll as well.
    if (!this.dragging) return
    if (event.cancelable) event.preventDefault()
    for (const touch of event.touches) {
      if (touch.identifier === this.dragTouchId) {
        this.pointerY = touch.clientY
        this.fingerAt = [touch.clientX, touch.clientY]
        this.notePointer(touch.clientX, touch.clientY)
        this.followCard()
      } else if (this.others.has(touch.identifier)) {
        const delta = this.others.get(touch.identifier) - touch.clientY
        if (!delta) continue

        // Pixels per millisecond, smoothed over the last moves.
        const elapsed = Math.max(event.timeStamp - this.lastMove, 1)
        this.velocity = 0.7 * (delta / elapsed) + 0.3 * (this.velocity || 0)
        this.lastMove = event.timeStamp
        this.momentum = 0
        this.handScrolled = (this.handScrolled || 0) + Math.abs(delta)
        this.scroll(delta)
        this.others.set(touch.identifier, touch.clientY)
      }
    }
  }

  // SortableJS follows pointers (iOS included): any pointer but the first is kept from it,
  // or its moves would drag the card along and its lift drop it. A mouse has only one.
  handlePointer(event) {
    if (event.type === 'pointerdown' && event.isPrimary && !this.dragging) this.dragPointerId = event.pointerId
    if (event.pointerId === this.dragPointerId) {
      if (event.pointerType === 'mouse' && event.type === 'pointermove') {
        this.pointerY = event.clientY
        this.notePointer(event.clientX, event.clientY)
      }
      return
    }
    if (this.dragging) event.stopImmediatePropagation()
  }

  // After a card has been filed, the next filing waits for the finger to move. Under a still
  // finger, the grid's relayout after a swap could bring another card under it (masonry
  // columns rebalance), hence another swap, and so on. Only the user's own moves count: the
  // finger, or a second-finger scroll and its momentum — not the edge scroll, which follows
  // the grid's new height after a swap.
  //
  // Nor straight back with the card just swapped with: once swapped, that card lies right
  // next to the finger, whose next few pixels crossed its middle again — the two cards
  // swapped back and forth.
  markSwap(card) {
    this.swapAt = this.pointerAt && [...this.pointerAt]
    this.swappedWith = card
    this.handScrolled = 0
  }

  allowsSwap(card) {
    if (!this.swapAt || !this.pointerAt) return true

    const [x, y] = this.swapAt
    const moved = Math.hypot(this.pointerAt[0] - x, this.pointerAt[1] - y)
    const limit = card === this.swappedWith ? SAME_CARD_DISTANCE : STILL_DISTANCE
    return moved > limit || this.handScrolled > limit
  }

  notePointer(x, y) {
    this.pointerAt = [x, y]
  }

  // True while the page scrolls by itself (edge, second finger, momentum), and a moment after:
  // the card then finds its place only once the page has stopped, as on the iOS home screen,
  // instead of being filed at every spot the page carried under it.
  get scrollingByHand() {
    // The momentum's slow tail lasts most of a second: the card may land during it.
    if (this.others.size || this.edgeMoving || Math.abs(this.momentum || 0) > SETTLED_SPEED) return true

    return performance.now() - (this.handStoppedAt || 0) < SETTLE_DELAY
  }

  tick = (time) => {
    if (!this.dragging) return

    const elapsed = this.lastFrame ? Math.min(time - this.lastFrame, 50) : 16
    this.lastFrame = time
    // Eased towards the speed the finger asks for: entering the band, or moving in it, no
    // longer jumps from one speed to another.
    const target = this.edgeSpeed()
    this.edgeCurrent = (this.edgeCurrent || 0) + (target - (this.edgeCurrent || 0)) * Math.min(elapsed / EDGE_EASING, 1)
    if (!target && Math.abs(this.edgeCurrent) < 5) this.edgeCurrent = 0
    // Moving only if the page really went: at its top or bottom, a finger in the band no
    // longer scrolls anything, and the card must be able to land there.
    const before = window.scrollY
    if (this.edgeCurrent) this.scroll((this.edgeCurrent * elapsed) / 1000)
    if (window.scrollY !== before) this.edgeMovedAt = time
    // Only while the finger is in the band: out of it, the card may land at once, even
    // while the scroll eases off.
    this.edgeMoving = !!target && time - (this.edgeMovedAt || 0) < EDGE_STILL
    this.followCard()
    if (this.momentum) {
      this.handScrolled = (this.handScrolled || 0) + Math.abs(this.momentum * elapsed)
      this.scroll(this.momentum * elapsed)
      this.momentum *= FRICTION ** (elapsed / 16)
      if (Math.abs(this.momentum) < 0.02) {
        this.momentum = 0
        this.handStoppedAt = performance.now()
      }
    }
    this.frame = requestAnimationFrame(this.tick)
  }

  // Pixels per second, negative upwards. The edges are those of what the page shows: in the
  // apps, the native bars cover its top and bottom, and a finger over them never reaches it.
  edgeSpeed() {
    if (this.pointerY == null || this.others.size) return 0

    const top = this.safeTop
    const bottom = window.innerHeight - this.safeBottom
    const zone = Math.min(EDGE_ZONE, (bottom - top) / 4)
    const depth = Math.max((top + zone - this.pointerY) / zone, (this.pointerY - (bottom - zone)) / zone)
    if (depth <= 0) return 0

    const speed = MAX_SPEED * Math.min(depth, 1) ** 2
    return this.pointerY < top + zone ? -speed : speed
  }

  // The carried card, fixed to the screen. On iOS SortableJS places its own in the page
  // (PositionGhostAbsolutely) and puts it back under the finger once the page has moved:
  // WebKit applies a scroll apart from the rest of the display, so the card went a few
  // pixels along with the page, then came back. Its own card stays, hidden, for its
  // computations; a copy fixed to the screen follows the finger instead.
  followCard() {
    if (!this.fingerAt) return

    if (!this.card) {
      const ghost = document.querySelector('.sortable-fallback')
      if (!ghost) return

      const rect = ghost.getBoundingClientRect()
      this.grab = [this.fingerAt[0] - rect.left, this.fingerAt[1] - rect.top]
      const card = ghost.cloneNode(true)
      card.removeAttribute('id')
      if (ghost.id.startsWith('product_')) card.classList.add('shadow-xl')
      Object.assign(card.style, {
        position: 'fixed', left: '0', top: '0', margin: '0', width: `${rect.width}px`, height: `${rect.height}px`,
        transition: 'none', pointerEvents: 'none', zIndex: '100000', opacity: '1',
      })
      document.body.appendChild(card)
      ghost.style.visibility = 'hidden'
      this.card = card
    }
    const [x, y] = this.fingerAt
    this.card.style.transform = `translate3d(${x - this.grab[0]}px, ${y - this.grab[1]}px, 0)`
  }

  // The aimed position keeps the fractions of each step (WebKit scrolls by whole pixels); it
  // follows the page again whenever something else moved it (a relayout, a restore).
  scroll(pixels) {
    if (this.aimed == null || Math.abs(window.scrollY - this.shown) > 2) this.aimed = window.scrollY
    this.aimed += pixels
    const position = Math.round(this.aimed)
    if (position !== this.shown) {
      window.scrollTo(0, position)
      this.shown = window.scrollY
      // At the top or bottom of the page, the aimed position stays where the page stopped:
      // carried on beyond it, it had to come all the way back before the page moved again.
      if (Math.abs(position - this.shown) > 1) this.aimed = this.shown
    }
  }
}

const TYPES = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'touchstart', 'touchmove', 'touchend', 'touchcancel']

// Height of the band, at each edge, where the page scrolls by itself.
const EDGE_ZONE = 140
// Speed at the very edge, in pixels per second.
const MAX_SPEED = 1600
// Share of its speed the page keeps from one frame to the next after a second-finger flick.
const FRICTION = 0.935
// Share of the finger's speed the momentum starts with, and its ceiling (pixels per ms):
// a quick flick carried the page several screens away.
const MOMENTUM_SHARE = 0.8
const MAX_MOMENTUM = 3
// Stillness, in milliseconds, before the card is filed again after a second-finger scroll.
const SETTLE_DELAY = 40
// Distance, in pixels, the finger or the page must move before another swap.
const STILL_DISTANCE = 8
// Distance, in pixels, the finger must move before swapping back with the same card.
const SAME_CARD_DISTANCE = 40
// Momentum below this (pixels per ms) lets the card land.
const SETTLED_SPEED = 0.25
// Time, in milliseconds, the edge scroll takes to reach the speed the finger asks for.
const EDGE_EASING = 100
// Without the page moving for this long (ms), the edge scroll counts as stopped (top or
// bottom of the page reached), even with the finger still in the band.
const EDGE_STILL = 100
